import { CognitoAccessToken, CognitoIdToken, CognitoRefreshToken, CognitoUserSession } from 'amazon-cognito-identity-js';
import { cognitoConfig } from './cognito-config.ts';
import { setLocalStorage, getLocalStorage, removeLocalStorage } from "../../utils/localStorage/LocalStorageService.js";
import axios from "axios";

class SSOAuthService {
  constructor() {}

  // Start OAuth2 login flow
  public initiateLogin = (idpIdentifier) => {
    const { domain, scope, redirectSignIn, responseType, clientId } = cognitoConfig.oauth;
    
    // Build the OAuth2 URL with all required parameters
    const oauthUrl = `https://${domain}/oauth2/authorize?response_type=${responseType}&client_id=${clientId}&redirect_uri=${redirectSignIn}&scope=${scope.join('+')}&idp_identifier=${idpIdentifier}`;
    
    // Redirect to the Cognito login page
    window.location.href = oauthUrl;
  };

  // Handle the callback from Cognito OAuth2 with authorization code
  public handleCallback = async (authorizationCode: string): Promise<CognitoUserSession> => {
    const { domain, redirectSignIn, clientId } = cognitoConfig.oauth;
    
    try {
      // Exchange the authorization code for tokens
      const tokenEndpoint = `https://${domain}/oauth2/token`;
      const params = new URLSearchParams();
      params.append("grant_type", 'authorization_code');
      params.append("client_id", clientId!);
      params.append("code", authorizationCode);
      params.append("redirect_uri", redirectSignIn!);

      const response = axios
            .post(
              tokenEndpoint,
              params,
              {
                headers: {
                  "Content-Type": "application/x-www-form-urlencoded"
                }
              }
            )

      if ((await response).status != 200) {
        throw new Error('Failed to exchange authorization code for tokens');
      }

      const tokens = (await response).data;

      let accessToken: CognitoAccessToken = new CognitoAccessToken({ AccessToken: tokens.access_token});
      let idToken: CognitoIdToken = new CognitoIdToken({ IdToken: tokens.id_token});
      let refreshToken: CognitoRefreshToken = new CognitoRefreshToken({ RefreshToken: tokens.refresh_token});
      let sessiondata = {
        IdToken: idToken,
		    RefreshToken: refreshToken,
		    AccessToken: accessToken,
      }
      let session: CognitoUserSession = new CognitoUserSession(sessiondata);
      this.setTokens(session);
 
      return session;
    } catch (error) {
      console.error('Error during token exchange:', error);
      throw error;
    }
  };

  // Log out the user
  public logout = () => {
    const idToken = this.getIdToken();
    if (idToken == undefined) return "";

    const { redirectSignOut, clientId, domain } = cognitoConfig.oauth;

    return `https://${domain}/logout?client_id=${clientId}&logout_uri=${redirectSignOut}`;
  };

  // Check if the user is authenticated
  public isAuthenticated = () => {
    const idToken = this.getIdToken();
    if (idToken == undefined) return false;
    
    try {
      // Check if the token is expired
      const currentTime = Math.floor(Date.now() / 1000);
      return idToken.payload.exp > currentTime;
    } catch (error) {
      return false;
    }
  };

  public getUser = (): CognitoUserSession | null => {
    let sessiondata = {
      IdToken: this.getIdToken(),
      RefreshToken: this.getRefreshToken(),
      AccessToken: this.getAccessToken(),
    }

    if (sessiondata.IdToken == undefined) return null;

    return new CognitoUserSession(sessiondata);
  }

  // Get user information from the token
  public getUserInfo = (): string | null => {
    const idToken = this.getIdToken();
    return idToken ? JSON.stringify(idToken.payload) : null;
  };

  // Store tokens in localStorage
  private setTokens = (session: CognitoUserSession): void => {
    setLocalStorage('idToken', JSON.stringify(session.getIdToken()));
    setLocalStorage('accessToken', JSON.stringify(session.getAccessToken()));
    setLocalStorage('refreshToken', JSON.stringify(session.getRefreshToken()));
  };

  // Clear tokens from localStorage
  public clearTokens = (): void => {
    removeLocalStorage('idToken');
    removeLocalStorage('accessToken');
    removeLocalStorage('refreshToken');
  };

  // Get the ID token from localStorage
  private getIdToken = (): CognitoIdToken => {
    let IdToken: string = getLocalStorage('idToken')!;
    if (IdToken == undefined) return IdToken;

    let parsed = JSON.parse(IdToken);
    let token: CognitoIdToken = new CognitoIdToken({ IdToken: parsed.jwtToken });
    return token;
  };

  private getAccessToken = (): CognitoAccessToken => {
    let AccessToken: string = getLocalStorage('accessToken')!;
    if (AccessToken == undefined) return AccessToken;

    let parsed = JSON.parse(AccessToken);
    let token: CognitoAccessToken = new CognitoAccessToken({ AccessToken: parsed.jwtToken });
    return token;
  };

  private getRefreshToken = (): CognitoRefreshToken => {
    let RefreshToken: string = getLocalStorage('refreshToken')!;
    if (RefreshToken == undefined) return RefreshToken;

    let parsed = JSON.parse(RefreshToken);
    let token: CognitoRefreshToken = new CognitoRefreshToken({ RefreshToken: parsed.token });
    return token;
  };
}

export default SSOAuthService;